import { useState, useEffect } from 'react';
import * as React from 'react';
import { FaPlus, FaTimes, FaPhone, FaHardHat, FaCheckCircle, FaUserTie, FaCog, FaChevronDown, FaChevronUp, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors } from '../../employees/styles/personalakteStyles';
import { fetchPositionsForWA, Position } from '../../contracts/services/ContractWizardService';
import WorkAreaService from '../../workareas/services/WorkAreaService';
import InterviewService from '../services/InterviewService';
import type {
  HiringPipeline,
  HiringPipelineStep,
  InterviewCriteriaTemplate,
  DEFAULT_PIPELINE_STEPS,
} from '../types';

interface WorkArea {
  id: number;
  name: string;
  color?: string;
  is_active?: boolean;
}

const STEP_PRESETS: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: 'phone_interview', label: 'Telefoninterview', icon: <FaPhone size={10} /> },
  { value: 'trial_shift', label: 'Probeschicht', icon: <FaHardHat size={10} /> },
  { value: 'in_person_interview', label: 'Vor-Ort-Interview', icon: <FaUserTie size={10} /> },
  { value: 'decision', label: 'Entscheidung', icon: <FaCheckCircle size={10} /> },
  { value: 'custom', label: 'Eigener Schritt', icon: <FaCog size={10} /> },
];

const STEP_ICON: Record<string, React.ReactNode> = {
  phone_interview: <FaPhone size={11} />,
  trial_shift: <FaHardHat size={11} />,
  in_person_interview: <FaUserTie size={11} />,
  decision: <FaCheckCircle size={11} />,
};

export default function RecruitingPipelineEditor(): React.JSX.Element {
  const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
  const [selectedWA, setSelectedWA] = useState<number | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loadingPositions, setLoadingPositions] = useState(false);

  // Pipeline state
  const [pipeline, setPipeline] = useState<HiringPipeline | null>(null);
  const [steps, setSteps] = useState<HiringPipelineStep[]>([]);
  const [loadingPipeline, setLoadingPipeline] = useState(false);
  const [pipelineDirty, setPipelineDirty] = useState(false);
  const [savingPipeline, setSavingPipeline] = useState(false);

  // Interview templates for this WA
  const [templates, setTemplates] = useState<InterviewCriteriaTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // UI
  const [expandedPosition, setExpandedPosition] = useState<number | null>(null);
  const [addStepPreset, setAddStepPreset] = useState('custom');

  useEffect(() => {
    WorkAreaService.fetchWorkAreas().then(was => {
      const active = was.filter(wa => wa.is_active !== false);
      setWorkAreas(active);
      if (active.length > 0 && !selectedWA) setSelectedWA(active[0].id);
    });
  }, []);

  // Load pipeline + positions + templates when WA changes
  useEffect(() => {
    if (!selectedWA) return;

    setLoadingPipeline(true);
    InterviewService.fetchPipelineByWorkArea(selectedWA)
      .then(p => {
        setPipeline(p);
        setSteps(p?.steps || [
          { step: 'phone_interview', label: 'Telefoninterview', required: true },
          { step: 'trial_shift', label: 'Probeschicht', required: true },
          { step: 'decision', label: 'Entscheidung', required: true },
        ]);
        setPipelineDirty(false);
      })
      .finally(() => setLoadingPipeline(false));

    setLoadingPositions(true);
    fetchPositionsForWA(selectedWA)
      .then(pos => setPositions(pos.filter(p => p.active !== false)))
      .catch(() => setPositions([]))
      .finally(() => setLoadingPositions(false));

    setLoadingTemplates(true);
    InterviewService.fetchTemplatesByWorkArea(selectedWA)
      .then(setTemplates)
      .finally(() => setLoadingTemplates(false));
  }, [selectedWA]);

  if (!selectedWA) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
        Keine Work Areas gefunden.
      </div>
    );
  }

  const selectedWAData = workAreas.find(wa => wa.id === selectedWA);
  const waColor = selectedWAData?.color || '#5271A5';

  // --- Pipeline handlers ---

  const handleAddStep = () => {
    const preset = STEP_PRESETS.find(p => p.value === addStepPreset);
    setSteps(prev => [...prev, {
      step: addStepPreset,
      label: preset?.label || 'Neuer Schritt',
      required: true,
    }]);
    setPipelineDirty(true);
  };

  const handleRemoveStep = (idx: number) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
    setPipelineDirty(true);
  };

  const handleMoveStep = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    setSteps(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
    setPipelineDirty(true);
  };

  const handleStepChange = (idx: number, updates: Partial<HiringPipelineStep>) => {
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...updates } : s));
    setPipelineDirty(true);
  };

  const handleSavePipeline = async () => {
    if (!selectedWA) return;
    setSavingPipeline(true);
    try {
      if (pipeline?.id) {
        const updated = await InterviewService.updatePipeline(pipeline.id, { steps });
        setPipeline(updated);
      } else {
        const created = await InterviewService.createPipeline({ work_area: selectedWA, steps });
        setPipeline(created);
      }
      setPipelineDirty(false);
      toast.success('Pipeline gespeichert');
    } catch (e: any) {
      console.error('[Pipeline] save error:', e);
      toast.error('Pipeline konnte nicht gespeichert werden');
    } finally {
      setSavingPipeline(false);
    }
  };

  // --- Template handlers ---

  const getTemplatesForPosition = (posLabel: string): InterviewCriteriaTemplate[] => {
    return templates.filter(t =>
      t.name.toLowerCase().includes(posLabel.toLowerCase()) ||
      t.criteria.some(c => c.label.toLowerCase().includes(posLabel.toLowerCase()))
    );
  };

  const handleCreateTemplate = async (posLabel: string) => {
    try {
      const created = await InterviewService.createTemplate({
        name: `${selectedWAData?.name || 'WA'} – ${posLabel}`,
        work_area: selectedWA,
        is_active: true,
        criteria: [],
        custom_questions: [],
      });
      setTemplates(prev => [...prev, created]);
      toast.success('Template erstellt');
    } catch (e: any) {
      toast.error('Template konnte nicht erstellt werden');
    }
  };

  const handleAddQuestion = async (templateId: number, question: string) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    const newQ = {
      key: `q_${Date.now()}`,
      label: question,
      type: 'textarea' as const,
    };
    try {
      const updated = await InterviewService.updateTemplate(templateId, {
        custom_questions: [...tpl.custom_questions, newQ],
      });
      setTemplates(prev => prev.map(t => t.id === templateId ? updated : t));
    } catch {
      toast.error('Frage konnte nicht gespeichert werden');
    }
  };

  const handleRemoveQuestion = async (templateId: number, questionKey: string) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    try {
      const updated = await InterviewService.updateTemplate(templateId, {
        custom_questions: tpl.custom_questions.filter(q => q.key !== questionKey),
      });
      setTemplates(prev => prev.map(t => t.id === templateId ? updated : t));
    } catch {
      toast.error('Frage konnte nicht entfernt werden');
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    fontSize: 12,
    color: colors.textBody,
    background: colors.bgCard,
    outline: 'none',
    width: '100%',
  };

  return (
    <div>
      {/* WA Selector Pills */}
      <div style={{
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        marginBottom: 20,
        padding: '12px 14px',
        background: colors.bgMuted,
        borderRadius: 12,
        border: `1px solid ${colors.borderLight}`,
      }}>
        {workAreas.map(wa => {
          const isActive = selectedWA === wa.id;
          const c = wa.color || '#5271A5';
          return (
            <button
              key={wa.id}
              onClick={() => { setSelectedWA(wa.id); setExpandedPosition(null); }}
              style={{
                padding: '6px 14px',
                borderRadius: 9999,
                border: isActive ? `2px solid ${c}` : `1px solid ${colors.border}`,
                background: isActive ? `${c}18` : 'rgba(255,255,255,0.9)',
                color: isActive ? c : colors.textBody,
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: c, flexShrink: 0,
              }} />
              {wa.name}
            </button>
          );
        })}
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Left: Pipeline Steps */}
        <div style={{
          background: colors.bgCard,
          borderRadius: 14,
          border: `1px solid ${colors.border}`,
          padding: '16px 18px',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 14,
          }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: colors.slate,
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Recruiting-Prozess
            </div>
            {pipelineDirty && (
              <button
                onClick={handleSavePipeline}
                disabled={savingPipeline}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 8,
                  background: waColor, border: 'none',
                  color: '#fff', fontSize: 10, fontWeight: 600,
                  cursor: 'pointer', opacity: savingPipeline ? 0.6 : 1,
                }}
              >
                <FaSave size={9} /> {savingPipeline ? 'Speichern...' : 'Speichern'}
              </button>
            )}
          </div>

          {loadingPipeline ? (
            <div style={{ fontSize: 12, color: colors.textMuted, padding: '12px 0' }}>Laden...</div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {steps.map((step, idx) => (
                  <div key={`${step.step}_${idx}`}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      background: colors.bgMuted,
                      borderRadius: 10,
                      border: `1px solid ${colors.borderLight}`,
                    }}>
                      {/* Step number */}
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%',
                        background: waColor,
                        color: '#fff', fontSize: 11, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {idx + 1}
                      </div>

                      {/* Icon */}
                      <span style={{ color: colors.textMuted, flexShrink: 0 }}>
                        {STEP_ICON[step.step] || <FaCog size={11} />}
                      </span>

                      {/* Editable label */}
                      <input
                        value={step.label}
                        onChange={e => handleStepChange(idx, { label: e.target.value })}
                        style={{
                          ...inputStyle,
                          border: 'none',
                          background: 'transparent',
                          fontWeight: 600,
                          padding: '2px 4px',
                        }}
                      />

                      {/* Required toggle */}
                      <button
                        onClick={() => handleStepChange(idx, { required: !step.required })}
                        title={step.required ? 'Pflicht' : 'Optional'}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 9, fontWeight: 600, flexShrink: 0,
                          color: step.required ? '#15803d' : colors.textMuted,
                          padding: '2px 4px',
                        }}
                      >
                        {step.required ? 'Pflicht' : 'Optional'}
                      </button>

                      {/* Move buttons */}
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button
                          onClick={() => handleMoveStep(idx, -1)}
                          disabled={idx === 0}
                          style={{
                            background: 'none', border: 'none',
                            cursor: idx === 0 ? 'default' : 'pointer',
                            color: idx === 0 ? colors.borderLight : colors.textMuted, padding: 2,
                          }}
                        >
                          <FaChevronUp size={9} />
                        </button>
                        <button
                          onClick={() => handleMoveStep(idx, 1)}
                          disabled={idx === steps.length - 1}
                          style={{
                            background: 'none', border: 'none',
                            cursor: idx === steps.length - 1 ? 'default' : 'pointer',
                            color: idx === steps.length - 1 ? colors.borderLight : colors.textMuted, padding: 2,
                          }}
                        >
                          <FaChevronDown size={9} />
                        </button>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => handleRemoveStep(idx)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: colors.textMuted, padding: 2, flexShrink: 0,
                        }}
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>

                    {/* Connector line */}
                    {idx < steps.length - 1 && (
                      <div style={{
                        width: 2, height: 12, background: colors.border,
                        marginLeft: 22, borderRadius: 1,
                      }} />
                    )}
                  </div>
                ))}
              </div>

              {/* Add step */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
                <select
                  value={addStepPreset}
                  onChange={e => setAddStepPreset(e.target.value)}
                  style={{ ...inputStyle, width: 'auto', flex: 1 }}
                >
                  {STEP_PRESETS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddStep}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '6px 12px', borderRadius: 8,
                    background: colors.bgMuted, border: `1px solid ${colors.border}`,
                    color: colors.slate, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  <FaPlus size={9} /> Schritt
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right: Interview Questions per Position */}
        <div style={{
          background: colors.bgCard,
          borderRadius: 14,
          border: `1px solid ${colors.border}`,
          padding: '16px 18px',
        }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: colors.slate,
            textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 14,
          }}>
            Interviewfragen nach Position
          </div>

          {loadingPositions || loadingTemplates ? (
            <div style={{ fontSize: 12, color: colors.textMuted, padding: '12px 0' }}>Laden...</div>
          ) : positions.length === 0 ? (
            <div style={{ fontSize: 12, color: colors.textMuted, padding: '12px 0', fontStyle: 'italic' }}>
              Keine Positionen für diese Work Area definiert.
              <br />
              <span style={{ fontSize: 11 }}>Positionen unter Organization &rarr; Work Areas anlegen.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {positions.map(pos => {
                const isExpanded = expandedPosition === pos.id;
                // Find template matching this position (by work_area + name)
                const posTemplates = templates.filter(t =>
                  t.work_area === selectedWA &&
                  t.name.toLowerCase().includes(pos.label.toLowerCase())
                );
                // Also show all WA templates if no position-specific ones
                const relevantTemplates = posTemplates.length > 0 ? posTemplates : templates.filter(t => t.work_area === selectedWA);
                const primaryTemplate = relevantTemplates[0];
                const questionCount = primaryTemplate?.custom_questions?.length || 0;

                return (
                  <div key={pos.id} style={{
                    borderRadius: 10,
                    border: `1px solid ${isExpanded ? waColor + '40' : colors.borderLight}`,
                    background: isExpanded ? `${waColor}06` : colors.bgMuted,
                    overflow: 'hidden',
                  }}>
                    {/* Position header */}
                    <button
                      onClick={() => setExpandedPosition(isExpanded ? null : pos.id)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '10px 12px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 12, color: colors.textPrimary }}>
                          {pos.label}
                        </span>
                        <span style={{
                          fontSize: 10, color: colors.textMuted,
                          padding: '1px 6px', background: 'rgba(255,255,255,0.8)',
                          borderRadius: 4, border: `1px solid ${colors.borderLight}`,
                        }}>
                          {questionCount} Fragen
                        </span>
                      </div>
                      {isExpanded ? <FaChevronUp size={10} color={colors.textMuted} /> : <FaChevronDown size={10} color={colors.textMuted} />}
                    </button>

                    {/* Expanded: questions */}
                    {isExpanded && (
                      <div style={{ padding: '0 12px 12px' }}>
                        {!primaryTemplate ? (
                          <div style={{ textAlign: 'center', padding: '12px 0' }}>
                            <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 8, fontStyle: 'italic' }}>
                              Noch kein Interview-Template für diese Position.
                            </div>
                            <button
                              onClick={() => handleCreateTemplate(pos.label)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '6px 12px', borderRadius: 8,
                                background: 'rgba(255,255,255,0.8)', border: `1px dashed ${colors.border}`,
                                color: colors.slate, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              <FaPlus size={9} /> Template erstellen
                            </button>
                          </div>
                        ) : (
                          <>
                            {primaryTemplate.custom_questions.length === 0 && (
                              <div style={{ fontSize: 11, color: colors.textMuted, padding: '8px 0', fontStyle: 'italic' }}>
                                Noch keine Fragen. Fragen hinzufügen, die beim Interview gestellt werden.
                              </div>
                            )}

                            {primaryTemplate.custom_questions.map((q, qi) => (
                              <div key={q.key} style={{
                                display: 'flex', gap: 8, alignItems: 'center',
                                padding: '6px 0',
                                borderBottom: qi < primaryTemplate.custom_questions.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                              }}>
                                <span style={{ fontSize: 10, color: colors.textMuted, fontWeight: 600, width: 16, textAlign: 'right', flexShrink: 0 }}>
                                  {qi + 1}.
                                </span>
                                <span style={{ flex: 1, fontSize: 12, color: colors.textBody }}>{q.label}</span>
                                <span style={{ fontSize: 10, color: colors.textMuted, padding: '1px 5px', background: colors.bgMuted, borderRadius: 4 }}>
                                  {q.type === 'textarea' ? 'Freitext' : q.type === 'select' ? 'Auswahl' : 'Text'}
                                </span>
                                <button
                                  onClick={() => handleRemoveQuestion(primaryTemplate.id, q.key)}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: colors.textMuted, padding: 2, flexShrink: 0,
                                  }}
                                >
                                  <FaTimes size={9} />
                                </button>
                              </div>
                            ))}

                            <AddQuestionInline
                              onAdd={(question) => handleAddQuestion(primaryTemplate.id, question)}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddQuestionInline({ onAdd }: { onAdd: (q: string) => void }) {
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 10px', borderRadius: 8, marginTop: 8,
          background: 'rgba(255,255,255,0.8)', border: `1px dashed ${colors.border}`,
          color: colors.slate, fontSize: 11, fontWeight: 600,
          cursor: 'pointer', width: '100%', justifyContent: 'center',
        }}
      >
        <FaPlus size={9} /> Frage hinzufügen
      </button>
    );
  }

  const handleSubmit = () => {
    if (!value.trim()) return;
    onAdd(value.trim());
    setValue('');
    setAdding(false);
  };

  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') setAdding(false); }}
        placeholder="Interviewfrage eingeben..."
        style={{
          flex: 1, padding: '6px 10px', borderRadius: 8,
          border: `1px solid ${colors.border}`, fontSize: 12,
          color: colors.textBody, background: colors.bgCard, outline: 'none',
        }}
      />
      <button
        onClick={handleSubmit}
        style={{
          padding: '6px 10px', borderRadius: 8,
          background: colors.slate, border: 'none',
          color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}
      >
        OK
      </button>
      <button
        onClick={() => { setValue(''); setAdding(false); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: colors.textMuted, padding: 2,
        }}
      >
        <FaTimes size={11} />
      </button>
    </div>
  );
}
